import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, RotateCcw, RefreshCw, ChevronDown, ChevronRight, User, UserMinus, X } from 'lucide-react';
import AppHeader from '../components/AppHeader';

// Method metadata mirrors OrderTracking's labels for consistency.
const METHODS = [
  { value: 'screen_print',   label: 'Screen Print',   emoji: '🖨', accent: '#0e7490', bg: '#ecfeff', border: '#a5f3fc' },
  { value: 'embroidery',     label: 'Embroidery',     emoji: '🧵', accent: '#7c2d12', bg: '#fff7ed', border: '#fed7aa' },
  { value: 'dtf',            label: 'DTF Transfer',   emoji: '✨', accent: '#6b21a8', bg: '#faf5ff', border: '#e9d5ff' },
  { value: 'dtg',            label: 'DTG',            emoji: '🎨', accent: '#0f766e', bg: '#f0fdfa', border: '#99f6e4' },
  { value: 'sublimation',    label: 'Sublimation',    emoji: '🌈', accent: '#9d174d', bg: '#fdf2f8', border: '#fbcfe8' },
  { value: 'heat_transfer',  label: 'Heat Transfer',  emoji: '🔥', accent: '#9a3412', bg: '#fff7ed', border: '#fdba74' },
  { value: 'vinyl',          label: 'Vinyl',          emoji: '🪧', accent: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' },
];
const methodMeta = (v) => METHODS.find((m) => m.value === v) || { value: v, label: (v || '').toUpperCase(), emoji: '•', accent: '#475569', bg: '#f8fafc', border: '#e2e8f0' };

const formatDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
};

const TaskCard = ({ task, currentUserEmail, onComplete, onReopen, onViewMockups, onClaim, onRelease }) => {
  const completed = task.status === 'completed';
  const assignee = task.assigned_to || null;
  const mine = assignee && assignee === currentUserEmail;
  const src = task.thumbnail
    ? (task.thumbnail.startsWith('data:') ? task.thumbnail : `data:image/png;base64,${task.thumbnail}`)
    : null;
  // Operators don't bother showing email if it's themselves — UI shows "ME" pill instead.
  const assigneeShort = assignee ? (assignee === currentUserEmail ? 'ME' : assignee.split('@')[0]) : null;
  return (
    <div
      data-testid={`task-card-${task.id}`}
      style={{
        display: 'flex', gap: 12, padding: 12,
        background: '#ffffff',
        border: `1px solid ${completed ? '#86efac' : mine ? '#a78bfa' : '#e2e8f0'}`,
        borderRadius: 10,
        marginBottom: 10,
        opacity: completed ? 0.78 : 1,
      }}
    >
      <button
        type="button"
        onClick={() => onViewMockups(task)}
        title="View all mockups"
        data-testid={`view-mockups-${task.id}`}
        style={{
          all: 'unset', cursor: 'pointer',
          width: 72, height: 72, flexShrink: 0,
          borderRadius: 8, overflow: 'hidden', background: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        {src ? (
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 10 }}>
            No design
          </div>
        )}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {task.brand} {task.style} · {task.color}
          </p>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>×{task.quantity}</span>
        </div>
        <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>
          {task.customer_name} · <span style={{ color: '#7c3aed', fontWeight: 600 }}>{task.po_number}</span>
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {(task.locations || []).map((loc) => (
            <span key={loc} style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
              background: '#f1f5f9', color: '#475569',
            }}>{loc}</span>
          ))}
          {task.stitch_count && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#fff7ed', color: '#7c2d12' }}>
              {task.stitch_count.toLocaleString()} stitches
            </span>
          )}
          {task.due_date && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>
              Due {formatDate(task.due_date)}
            </span>
          )}
          {assignee && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
              background: mine ? '#ede9fe' : '#dbeafe', color: mine ? '#6d28d9' : '#1d4ed8',
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}
              title={mine ? `Assigned to you (${currentUserEmail})` : `Assigned to ${assignee}`}
              data-testid={`task-assignee-${task.id}`}>
              <User size={10} />{assigneeShort}
            </span>
          )}
        </div>
        {completed && task.completed_at && (
          <p style={{ fontSize: 10, color: '#16a34a', margin: '6px 0 0', fontWeight: 600 }}>
            ✓ Completed {formatDate(task.completed_at)}{task.completed_by ? ` · ${task.completed_by}` : ''}
          </p>
        )}
        {task.decoration_notes && (
          <div style={{
            marginTop: 6, padding: '6px 8px', background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: 6, fontSize: 11, color: '#78350f', lineHeight: 1.4,
          }} data-testid={`task-notes-${task.id}`}>
            <strong style={{ fontSize: 10, color: '#92400e' }}>NOTES:</strong> {task.decoration_notes}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center', minWidth: 110 }}>
        {!completed && (
          assignee ? (
            mine ? (
              <Button
                size="sm" variant="outline"
                onClick={() => onRelease(task)}
                data-testid={`release-task-${task.id}`}
                style={{ fontSize: 11, height: 26, color: '#475569' }}>
                <UserMinus size={11} style={{ marginRight: 4 }} />Release
              </Button>
            ) : null
          ) : (
            <Button
              size="sm" variant="outline"
              onClick={() => onClaim(task)}
              data-testid={`claim-task-${task.id}`}
              style={{ fontSize: 11, height: 26, color: '#6d28d9', borderColor: '#c4b5fd' }}>
              <User size={11} style={{ marginRight: 4 }} />Claim
            </Button>
          )
        )}
        {!completed ? (
          <Button
            size="sm"
            onClick={() => onComplete(task)}
            data-testid={`complete-task-${task.id}`}
            style={{ background: '#16a34a', color: '#fff', fontSize: 12, height: 30 }}
          >
            <Check size={14} style={{ marginRight: 4 }} />Mark Done
          </Button>
        ) : (
          <Button
            size="sm" variant="outline"
            onClick={() => onReopen(task)}
            data-testid={`reopen-task-${task.id}`}
            style={{ fontSize: 12, height: 28, color: '#475569' }}
          >
            <RotateCcw size={12} style={{ marginRight: 4 }} />Reopen
          </Button>
        )}
      </div>
    </div>
  );
};

// Read-only, full-size mockup viewer for the print floor — every angle,
// print sizes, and decoration notes. No editing tools needed to print.
const MockupViewerModal = ({ viewer, onClose, onOpenDesigner, isAdmin }) => {
  if (!viewer) return null;
  const { task, loading, data } = viewer;
  const imgSrc = (v) => (v && !v.startsWith('data:') && !v.startsWith('http') ? `data:image/png;base64,${v}` : v);
  const order = ['front', 'back', 'left', 'right'];
  const keys = data ? [
    ...order.filter((k) => data.views?.[k]),
    ...Object.keys(data.views || {}).filter((k) => !order.includes(k)),
  ] : [];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.8)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose} data-testid="mockup-viewer-modal">
      <div style={{ background: '#fff', width: '100%', maxWidth: 1000, maxHeight: '94vh', overflowY: 'auto', borderRadius: 14 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
              {data ? `${data.brand} ${data.style} · ${data.color}` : `${task.brand || ''} ${task.style || ''}`}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              ×{data?.quantity ?? task.quantity} pcs · {(data?.method || task.method || '').replace('_', ' ')}
              {task.customer_name ? ` · ${task.customer_name}` : ''}{task.po_number ? ` · ${task.po_number}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isAdmin && (
              <button onClick={() => onOpenDesigner(task)} data-testid="viewer-open-designer"
                style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}>
                Open in Designer
              </button>
            )}
            <button onClick={onClose} data-testid="viewer-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
              <X size={20} color="#64748b" />
            </button>
          </div>
        </div>
        <div style={{ padding: 22 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading mockups…</div>
          ) : keys.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>No mockup saved for this item yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: keys.length > 1 ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr', gap: 18 }}>
              {keys.map((k) => (
                <figure key={k} style={{ margin: 0 }} data-testid={`viewer-view-${k}`}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 8 }}>
                    <img src={imgSrc(data.views[k])} alt={k}
                      style={{ width: '100%', maxHeight: '58vh', objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
                      onClick={() => window.open(imgSrc(data.views[k]), '_blank')} />
                  </div>
                  <figcaption style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 6, textAlign: 'center' }}>
                    {k}{data.print_sizes?.[k] ? ` — ${data.print_sizes[k]}` : ''}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          {data?.decoration_notes && (
            <div style={{ marginTop: 18, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e' }} data-testid="viewer-notes">
              <strong style={{ fontSize: 11 }}>NOTES:</strong> {data.decoration_notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const ProductionQueues = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [queues, setQueues] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState({}); // method -> bool
  const [searchTerm, setSearchTerm] = useState('');
  // Filter mode: 'all' = everything · 'mine' = assigned-to-me OR completed-by-me · 'unassigned' = only no assignee.
  const [filterMode, setFilterMode] = useState('all');
  const me = user?.email || '';

  const fetchQueues = async () => {
    try {
      const r = await axios.get('/production/queues');
      setQueues(r.data?.queues || {});
    } catch (e) {
      toast.error('Failed to load production queues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueues(); }, []);

  const syncQueues = async () => {
    try {
      const r = await axios.post('/production/sync');
      toast.success(`Synced · ${r.data?.tasks_created || 0} new tasks`);
      await fetchQueues();
    } catch {
      toast.error('Sync failed');
    }
  };

  const completeTask = async (task) => {
    try {
      const r = await axios.post(`/production/tasks/${task.id}/complete`);
      toast.success(r.data?.order_status === 'Ready for Pickup' ? 'Done · All tasks complete — order is Ready for Pickup' : r.data?.remaining_tasks ? `Marked done · ${r.data.remaining_tasks} task(s) left on this order` : 'Marked done');
      await fetchQueues();
    } catch {
      toast.error('Could not complete task');
    }
  };

  const reopenTask = async (task) => {
    try {
      await axios.post(`/production/tasks/${task.id}/reopen`);
      toast.message('Task reopened');
      await fetchQueues();
    } catch {
      toast.error('Could not reopen task');
    }
  };

  const openDesigner = (task) => {
    navigate(`/mockup-designer?proposal=${task.proposal_id}&item=${task.item_index}`);
  };

  const [viewer, setViewer] = useState(null); // { task, loading, data }
  const openMockups = async (task) => {
    setViewer({ task, loading: true, data: null });
    try {
      const r = await axios.get(`/production/tasks/${task.id}/mockups`);
      setViewer({ task, loading: false, data: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load mockups');
      setViewer(null);
    }
  };

  const claimTask = async (task) => {
    try {
      await axios.post(`/production/tasks/${task.id}/assign`, { assigned_to: me });
      toast.success('Claimed — added to My Queue');
      await fetchQueues();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not claim');
    }
  };

  const releaseTask = async (task) => {
    try {
      await axios.post(`/production/tasks/${task.id}/assign`, { assigned_to: null });
      toast.message('Released');
      await fetchQueues();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not release');
    }
  };

  // Filter: hide methods with no activity unless search is active.
  const visibleMethods = useMemo(() => {
    const lower = searchTerm.toLowerCase().trim();
    const passesFilter = (t) => {
      if (filterMode === 'mine') {
        return t.assigned_to === me || t.completed_by === me;
      }
      if (filterMode === 'unassigned') {
        return !t.assigned_to && t.status !== 'completed';
      }
      return true;
    };
    const passesSearch = (t) => {
      if (!lower) return true;
      return (
        (t.po_number || '').toLowerCase().includes(lower) ||
        (t.customer_name || '').toLowerCase().includes(lower) ||
        (t.brand || '').toLowerCase().includes(lower) ||
        (t.style || '').toLowerCase().includes(lower) ||
        (t.color || '').toLowerCase().includes(lower) ||
        (t.assigned_to || '').toLowerCase().includes(lower)
      );
    };
    return METHODS
      .map((m) => {
        const bucket = queues[m.value] || { pending: [], completed: [] };
        const matches = (t) => passesFilter(t) && passesSearch(t);
        return {
          ...m,
          pending: (bucket.pending || []).filter(matches),
          completed: (bucket.completed || []).filter(matches),
        };
      })
      .filter((m) => m.pending.length > 0 || m.completed.length > 0);
  }, [queues, searchTerm, filterMode, me]);

  const totalPending = visibleMethods.reduce((s, m) => s + m.pending.length, 0);
  const totalCompleted = visibleMethods.reduce((s, m) => s + m.completed.length, 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <AppHeader user={user} onLogout={logout} />

      <main style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 800, margin: 0, color: '#0f172a' }}>Production Queues</h1>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
              One queue per decoration method. {totalPending} pending · {totalCompleted} completed (last 30 each)
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 999, padding: 3, gap: 2 }}>
              {[
                { value: 'all', label: 'All' },
                { value: 'mine', label: 'My Queue' },
                { value: 'unassigned', label: 'Unassigned' },
              ].map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilterMode(f.value)}
                  data-testid={`filter-${f.value}`}
                  style={{
                    all: 'unset', cursor: 'pointer',
                    padding: '6px 14px', borderRadius: 999,
                    fontSize: 12, fontWeight: 700,
                    background: filterMode === f.value ? '#0f172a' : 'transparent',
                    color: filterMode === f.value ? '#fff' : '#475569',
                  }}>
                  {f.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search PO, customer, style, operator…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="production-search"
              style={{
                padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                fontSize: 13, width: 240, background: '#fff',
              }}
            />
            <Button variant="outline" onClick={syncQueues} data-testid="production-sync-btn">
              <RefreshCw size={14} style={{ marginRight: 6 }} />Sync
            </Button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#64748b' }}>Loading queues…</p>
        ) : visibleMethods.length === 0 ? (
          <div style={{ background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 48, textAlign: 'center' }}>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', margin: 0 }}>
              {filterMode === 'mine' ? 'Nothing in your queue yet' :
               filterMode === 'unassigned' ? 'No unassigned tasks' :
               'No active production tasks'}
            </p>
            <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0' }}>
              {filterMode === 'mine'
                ? 'Switch to "Unassigned" and click Claim on a task to add it to your queue.'
                : 'Tasks appear here once an order\'s designs are approved. Try clicking Sync.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, alignItems: 'flex-start' }}>
            {visibleMethods.map((m) => {
              const expanded = showCompleted[m.value] ?? false;
              return (
                <section
                  key={m.value}
                  data-testid={`queue-${m.value}`}
                  style={{
                    background: '#ffffff', borderRadius: 14,
                    border: `1px solid ${m.border}`,
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  }}
                >
                  <header style={{
                    background: m.bg, padding: '12px 14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: `1px solid ${m.border}`,
                  }}>
                    <div>
                      <p style={{ fontSize: 11, fontWeight: 700, color: m.accent, margin: 0, letterSpacing: 0.5 }}>
                        {m.emoji} {m.label.toUpperCase()}
                      </p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: '2px 0 0' }}>
                        {m.pending.length} <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>pending</span>
                      </p>
                    </div>
                    {m.completed.length > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '4px 8px', borderRadius: 999 }}>
                        ✓ {m.completed.length} done
                      </span>
                    )}
                  </header>

                  <div style={{ padding: 12 }}>
                    {m.pending.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0', fontStyle: 'italic' }}>Nothing pending.</p>
                    ) : (
                      m.pending.map((t) => (
                        <TaskCard key={t.id} task={t} currentUserEmail={me}
                          onComplete={completeTask} onReopen={reopenTask}
                          onViewMockups={openMockups}
                          onClaim={claimTask} onRelease={releaseTask} />
                      ))
                    )}

                    {m.completed.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowCompleted((s) => ({ ...s, [m.value]: !expanded }))}
                          data-testid={`toggle-completed-${m.value}`}
                          style={{
                            all: 'unset', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 12, fontWeight: 700, color: '#16a34a',
                            padding: '8px 4px', marginTop: 4, width: '100%',
                          }}
                        >
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          Completed ({m.completed.length})
                        </button>
                        {expanded && m.completed.map((t) => (
                          <TaskCard key={t.id} task={t} currentUserEmail={me}
                            onComplete={completeTask} onReopen={reopenTask}
                            onViewMockups={openMockups}
                            onClaim={claimTask} onRelease={releaseTask} />
                        ))}
                      </>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      <MockupViewerModal
        viewer={viewer}
        onClose={() => setViewer(null)}
        onOpenDesigner={openDesigner}
        isAdmin={user?.role === 'admin'}
      />
    </div>
  );
};

export default ProductionQueues;
